import React, { useState } from "react";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

const localeMap = { fr: "fr-FR", th: "th-TH", en: "en-US" };
const typeColor = { monthly: "bg-teak text-white", weekly: "bg-terracotta text-white", daily: "bg-gold text-[#2A2A2A]" };

const dayMs = 86400000;
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const parseDate = (s) => { if (!s) return null; const d = new Date(String(s).slice(0, 10)); return isNaN(d) ? null : d; };

export const ReservationTimeline = ({ reservations, tenants, units }) => {
  const { t, lang } = useI18n();
  const [anchor, setAnchor] = useState(startOfMonth(new Date()));

  const viewStart = startOfMonth(anchor);
  const daysInMonth = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0).getDate();
  const viewEnd = new Date(anchor.getFullYear(), anchor.getMonth(), daysInMonth);
  const monthLabel = anchor.toLocaleDateString(localeMap[lang] || "fr-FR", { month: "long", year: "numeric" });
  const today = new Date();

  const tName = (id) => { const tn = tenants.find((x) => x.id === id); return tn ? `${tn.first_name} ${tn.last_name}` : "—"; };
  const shift = (n) => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + n, 1));

  const barFor = (r) => {
    if (!["active", "upcoming"].includes(r.status)) return null;
    const s = parseDate(r.start_date);
    if (!s) return null;
    let e = parseDate(r.end_date) || viewEnd;
    if (e > viewEnd) e = viewEnd;
    let bs = s < viewStart ? viewStart : s;
    if (bs > viewEnd || e < viewStart) return null;
    const offset = Math.round((bs - viewStart) / dayMs);
    const dur = Math.max(1, Math.round((e - bs) / dayMs) + 1);
    return { left: (offset / daysInMonth) * 100, width: (dur / daysInMonth) * 100, r };
  };

  const todayPct = (today >= viewStart && today <= viewEnd) ? ((today.getDate() - 1) / daysInMonth) * 100 : null;

  return (
    <div className="space-y-4" data-testid="reservation-timeline">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => shift(-1)} data-testid="timeline-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <div className="font-semibold text-teak capitalize min-w-[160px] text-center">{monthLabel}</div>
          <Button variant="outline" size="icon" onClick={() => shift(1)} data-testid="timeline-next"><ChevronRight className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" onClick={() => setAnchor(startOfMonth(new Date()))} data-testid="timeline-today">{t("res.today")}</Button>
        </div>
        <div className="flex gap-3 text-xs">
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-teak" />{t("res.monthly")}</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-terracotta" />{t("res.weekly")}</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-gold" />{t("res.daily")}</span>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-white overflow-hidden">
        {units.filter((u) => u.type !== "conciergerie").map((u, idx, arr) => {
          const bars = reservations.filter((r) => r.unit_id === u.id).map(barFor).filter(Boolean);
          return (
            <div key={u.id} className={`flex items-stretch ${idx !== arr.length - 1 ? "border-b border-border" : ""}`} data-testid={`timeline-row-${u.number}`}>
              <div className="w-24 shrink-0 px-3 py-3 border-r border-border bg-sand/40 flex flex-col justify-center">
                <div className="font-semibold text-sm">{u.number}</div>
                <div className="text-[10px] text-muted-foreground truncate">{u.name}</div>
              </div>
              <div className="relative flex-1 h-14">
                {todayPct !== null && (
                  <div className="absolute top-0 bottom-0 w-px bg-terracotta/50 z-0" style={{ left: `${todayPct}%` }} />
                )}
                {bars.length === 0 && (
                  <div className="absolute inset-0 flex items-center justify-center text-[11px] text-[#2D6A4F]/60">{t("res.available")}</div>
                )}
                {bars.map((b, i) => (
                  <div key={i} title={`${tName(b.r.tenant_id)}`}
                    className={`absolute top-1/2 -translate-y-1/2 h-8 rounded-md px-2 flex items-center text-xs font-medium shadow-sm z-10 overflow-hidden ${typeColor[b.r.rental_type] || "bg-teak text-white"} ${b.r.status === "upcoming" ? "opacity-70 border border-dashed border-white/60" : ""}`}
                    style={{ left: `${b.left}%`, width: `calc(${b.width}% - 4px)`, marginLeft: "2px" }}
                    data-testid={`timeline-bar-${b.r.id}`}>
                    <span className="truncate">{tName(b.r.tenant_id)}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
